// OWNER/GC SIGN-OFF TOGGLE (owner, 2026-10-21: "I can go to the level, areas,
// systems, and choose to toggle some of them ... make sure there is an
// optional date on each toggle, so when the blue star shows up on the
// schedule, either it picks today's date or if date is entered, it will be
// placed on the picked date").
//
// One switch for one level, area or system, with its optional day beside it.
// Switched on, it says the day, who marked it and what it reaches (schedule
// rows starred, issues and shop drawings signed off) - the server counts
// those, so the numbers are the ones the schedule and the lists show.
//
// `toggle` is the row from GET /owner-sign-offs for this target, or null.
// `onSet({ date })` switches it on or moves its day; `onClear()` switches it
// off. Both are the page's; this only draws.
import React, { useState } from "react";
import { Switch, Tooltip } from "antd";

export const OWNER_STAR_BLUE = "#1d4ed8";
export const OWNER_STAR_PLANNED = "#93c5fd";

/** The toggle answering one target, from the list (null when off). */
export function findToggle(toggles, { kind, level = "", areaId = "", areaSetId = "", system = "" }) {
  const key = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
  const lvl = (s) => {
    const k = key(s);
    const m = /^(?:level|lvl|l|floor|fl)?0*(\d{1,3})$/.exec(k);
    return m ? `l${Number(m[1])}` : k;
  };
  return (toggles || []).find((t) => t.kind === kind
    && lvl(t.level) === lvl(level)
    && (kind === "level" || String(t.areaId || "") === String(areaId || "") || (t.areaSetId && String(t.areaSetId) === String(areaSetId || areaId || "")))
    && (kind !== "system" || key(t.system) === key(system))) || null;
}

/** "3 schedule rows · 12 issues · 2 shop drawings" - what the toggle reaches. */
export function reachLine(t) {
  if (!t) return "";
  const n = (v, one, many) => `${v} ${v === 1 ? one : many}`;
  return [
    n(t.activities || 0, "schedule row", "schedule rows"),
    n(t.issues || 0, "issue", "issues"),
    n(t.drawings || 0, "shop drawing", "shop drawings"),
  ].join(" · ");
}

export default function OwnerSignOffToggle({ toggle, canSign = false, label = "Signed off by owner/GC", size = "small", onSet, onClear }) {
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState("");
  const on = !!toggle;
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };
  const tip = on
    ? `${toggle.planned ? "Planned for" : "Signed off"} ${toggle.date}${toggle.by ? ` by ${toggle.by}` : ""} - ${reachLine(toggle)}`
    : canSign ? "Mark the client's sign-off. Leave the day empty for today, or pick the day the star should stand on." : "Not signed off by the owner/GC";
  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 12 }}
      data-testid="owner-signoff-toggle"
    >
      <Tooltip title={tip}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Switch
            size={size}
            checked={on}
            loading={busy}
            disabled={!canSign}
            aria-label={label}
            style={on ? { background: toggle.planned ? OWNER_STAR_PLANNED : OWNER_STAR_BLUE } : undefined}
            onChange={(v) => run(() => (v ? onSet({ date: day }) : onClear(toggle)))}
          />
          <span style={{ color: on ? OWNER_STAR_BLUE : "#64748b", fontWeight: on ? 600 : 400 }}>
            <span aria-hidden style={{ color: on ? (toggle.planned ? OWNER_STAR_PLANNED : OWNER_STAR_BLUE) : "#cbd5e1" }}>★</span> {label}
          </span>
        </span>
      </Tooltip>
      {canSign && (
        <input
          type="date"
          aria-label={`${label} - day`}
          title="Optional. Empty = today."
          disabled={busy}
          value={on ? toggle.date : day}
          onChange={(e) => {
            const v = e.target.value;
            if (on) { if (v) run(() => onSet({ date: v })); } else setDay(v);
          }}
          style={{ fontSize: 11.5, padding: "0 4px", height: 22, border: "1px solid #d9d9d9", borderRadius: 4, color: "#334155" }}
        />
      )}
      {on && !canSign && <span style={{ color: "#64748b" }}>{toggle.date}</span>}
      {on && (
        <span style={{ color: "#64748b", fontSize: 11 }} data-testid="owner-signoff-reach">
          {toggle.planned ? "planned · " : ""}{reachLine(toggle)}
        </span>
      )}
    </div>
  );
}
